import * as React from 'react';
import { Dispatch } from 'react-redux';
import { actions } from 'react-redux-form';
import { toast } from 'react-toastify';
import { Action } from 'redux';

import { AppState, history } from '..';

import { ToastComponent, ToastType } from '@common/components';
import { requestAction } from '@common/components/common.actions';
import { ToastOptions } from '@common/components/toast/toast';
import FormNames from '@constants/FormNames.enum';
import { AppDispatcher, OptionsViewModel } from '@models/common.models';
import {
  VendorViewModel,
  VendorPacketEventViewModel,
  ZipCountyOptionsViewModel,
  ExcludedZipViewModel,
  TradeViewModel
} from '@models/Vendor';
import { vendorService } from '@services/VendorService';

import { VendorState } from './vendor.reducer';
import { getRoute } from '@common/route-definitions';
import { RouteDefinitions } from '../common/route-definitions';
import { documentService } from '@services/DocumentService';
import { DocumentViewModel, FileViewModel } from '@models/Document';
import { cviService } from '@services/CVIService';
import { Option } from 'react-select';

export enum VendorActionType {
  ClearVendor = 'ClearVendor',
  GetVendorDetailsSuccess = 'GetVendorDetailsSuccess',
  GetZipCountyOptionsSuccess = 'GetZipCountyOptionsSuccess',
  GetExclusionZipCountyOptionsSuccess = 'GetExclusionZipCountyOptionsSuccess',
  SaveIncludedZipsSuccess = 'SaveIncludedZipsSuccess',
  SaveExcludedZipsSuccess = 'SaveExcludedZipsSuccess',
  GetVendorFilesSuccess = 'GetVendorFilesSuccess',
  SaveVendorFilesSuccess = 'SaveVendorFilesSuccess',
  RemoveVendorFilesSuccess = 'RemoveVendorFilesSuccess',
  SetVendorFilesLoading = 'SetVendorFilesLoading',
  SetZipsSaving = 'SetZipsSaving',
  SaveFormData = 'SaveFormData',
  SaveVendorFailure = 'SaveVendorFailure',
  SaveVendorStart = 'SaveVendorStart',
  SaveVendorSuccess = 'SaveVendorSuccess',
  SetVendorLoading = 'SetVendorLoading',
  SetPacketEvents = 'SetPacketEvents',
  GetVendorBrandCertificationsSuccess = 'GetVendorBrandCertificationsSuccess',
  UpdateVendorBrandCertificationsSuccess = 'UpdateVendorBrandCertificationsSuccess',
  GetEligibleBrandCertificationsSuccess = 'GetEligibleBrandCertificationsSuccess',
  GetVendorBrandCertificationsStart = 'GetVendorBrandCertificationsStart',
  GetVendorCVIExceptionsSuccess = 'GetVendorCVIExceptionsSuccess',
  DeleteVendorCVIExceptionsSuccess = 'DeleteVendorCVIExceptionsSuccess',
  UpdateVendorCVIExceptionsSuccess = 'UpdateVendorCVIExceptionsSuccess',
  GetVendorCVIExceptionsStart = 'GetVendorCVIExceptionsStart',
  GetVendorBrandExceptionsSuccess = 'GetVendorBrandExceptionsSuccess',
  DeleteVendorBrandExceptionsSuccess = 'DeleteVendorBrandExceptionsSuccess',
  UpdateVendorBrandExceptionsSuccess = 'UpdateVendorBrandExceptionsSuccess',
  SaveVendorTradesSuccess = 'SaveVendorTradesSuccess',
  UpdateVendorTradeSuccess = 'UpdateVendorTradeSuccess',
  ToggleTradeActivationSuccess = 'ToggleTradeActivationSuccess',
  GetVendorBrandExceptionsStart = 'GetVendorBrandExceptionsStart',
  ToggleTradeActivationStart = 'ToggleTradeActivationStart',
  GetExcludedZipsSuccess = 'GetExcludedZipsSuccess',
  AddZipCode = 'AddZipCode',
  RemoveZipCode = 'RemoveZipCode',
  SetTripCharges = 'SetTripCharges'
}

interface VendorAction extends Action {
  readonly type: VendorActionType;
}

export interface ClearVendorAction extends VendorAction {}

export interface GetExcludedZipsSuccessAction extends VendorAction {
  readonly excludedZips: ExcludedZipViewModel[];
}

export interface GetVendorDetailsSuccessAction extends VendorAction {
  readonly vendorVM: VendorViewModel;
}

export interface GetZipCountyOptionsSuccessAction extends VendorAction {
  readonly zipCountyOptions: ZipCountyOptionsViewModel[];
}

export interface GetExclusionZipCountyOptionsSuccessAction extends VendorAction {
  readonly exclusionZipCountyOptions: ZipCountyOptionsViewModel[];
}

export interface SaveIncludedZipsSuccessAction extends VendorAction {
  readonly includedZips: string[];
}

export interface DeleteIncludedZipsSuccessAction extends VendorAction {
  readonly includedZips: string[];
}
export interface DeleteExcludedZipsSuccessAction extends VendorAction {
  readonly excludedZips: ExcludedZipViewModel[];
}

export interface SaveExcludedZipsSuccessAction extends VendorAction {
  readonly excludedZips: ExcludedZipViewModel[];
}
export interface GetVendorFilesSuccessAction extends VendorAction {
  readonly documents: DocumentViewModel[];
}

export interface SaveVendorFilesSuccessAction extends VendorAction {
  readonly vendorId: number;
  readonly documents: DocumentViewModel[];
}

export interface RemoveVendorFileSuccessAction extends VendorAction {
  readonly vendorId: number;
  readonly fileId: number;
}

export interface SetVendorFilesLoadingAction extends Action {
  readonly type: VendorActionType.SetVendorFilesLoading;
  readonly isLoading: boolean;
}

export interface SetZipsSavingAction extends Action {
  readonly type: VendorActionType.SetZipsSaving;
  readonly isSaving: boolean;
}

export interface SaveFormDataAction extends VendorAction {
  readonly partialVendorVM: Partial<VendorViewModel>;
}

export interface SaveVendorFailureAction extends VendorAction {
  readonly errorMessage: string;
}
export interface SaveVendorStartAction extends VendorAction {}

export interface SaveVendorSuccessAction extends VendorAction {
  readonly createdID: number;
}

export interface SetVendorLoadingAction extends VendorAction {
  readonly type: VendorActionType.SetVendorLoading;
  readonly isVendorLoading: boolean;
}

export interface GetVendorCVIExceptionsStartAction extends VendorAction {
  readonly type: VendorActionType.GetVendorCVIExceptionsStart;
}
export interface GetVendorCVIExceptionsSuccessAction extends VendorAction {
  readonly type: VendorActionType.GetVendorCVIExceptionsSuccess;
  readonly cvis: OptionsViewModel[];
}

export interface UpdateVendorCVIExceptionsSuccessAction extends VendorAction {
  readonly type: VendorActionType.UpdateVendorCVIExceptionsSuccess;
  readonly cvis: OptionsViewModel[];
}

export interface DeleteVendorCVIExceptionsSuccessAction extends VendorAction {
  readonly type: VendorActionType.DeleteVendorCVIExceptionsSuccess;
  readonly cvis: OptionsViewModel[];
}

export interface GetVendorBrandExceptionsStartAction extends VendorAction {
  readonly type: VendorActionType.GetVendorBrandExceptionsStart;
}
export interface GetVendorBrandExceptionsSuccessAction extends VendorAction {
  readonly type: VendorActionType.GetVendorBrandExceptionsSuccess;
  readonly brands: OptionsViewModel[];
}

export interface UpdateVendorBrandExceptionsSuccessAction extends VendorAction {
  readonly type: VendorActionType.UpdateVendorBrandExceptionsSuccess;
  readonly brands: OptionsViewModel[];
}

export interface SaveVendorTradesSuccessAction extends VendorAction {
  readonly type: VendorActionType.SaveVendorTradesSuccess;
  readonly vendorTrades: TradeViewModel[];
}

export interface UpdateVendorTradeSuccessAction extends VendorAction {
  readonly type: VendorActionType.UpdateVendorTradeSuccess;
  readonly vendorTrade: TradeViewModel;
}

export interface ToggleTradeActivationStartAction extends VendorAction {
  readonly type: VendorActionType.ToggleTradeActivationStart;
}

export interface ToggleTradeActivationSuccessAction extends VendorAction {
  readonly type: VendorActionType.ToggleTradeActivationSuccess;
  readonly vendorTradeId: number;
  readonly activate: boolean;
}

export interface DeleteVendorBrandExceptionsSuccessAction extends VendorAction {
  readonly type: VendorActionType.DeleteVendorBrandExceptionsSuccess;
  readonly brands: OptionsViewModel[];
}

export interface GetVendorBrandCertificationsSuccessAction extends VendorAction {
  readonly type: VendorActionType.GetVendorBrandCertificationsSuccess;
  readonly brandCertifications: OptionsViewModel[];
}

export interface GetVendorBrandCertificationsStartAction extends VendorAction {
  readonly type: VendorActionType.GetVendorBrandCertificationsStart;
}

export interface GetEligibleBrandCertificationsSuccessAction extends VendorAction {
  readonly type: VendorActionType.GetEligibleBrandCertificationsSuccess;
  readonly brandCertifications: OptionsViewModel[];
}

export interface UpdateVendorBrandCertificationsSuccessAction extends VendorAction {
  readonly type: VendorActionType.UpdateVendorBrandCertificationsSuccess;
  readonly brandCertifications: OptionsViewModel[];
}

export interface GetEligibleBrandCertificationsSuccessAction extends VendorAction {
  readonly type: VendorActionType.GetEligibleBrandCertificationsSuccess;
  readonly brandCertifications: OptionsViewModel[];
}

export interface SetTripChargesAction extends VendorAction {
  readonly type: VendorActionType.SetTripCharges;
  readonly tripCharges: ExcludedZipViewModel[];
}

export function clearVendor(): ClearVendorAction {
  return {
    type: VendorActionType.ClearVendor
  };
}
export function clearVendorFull() {
  return async (dispatch: Dispatch<VendorState>) => {
    dispatch(actions.reset(FormNames.VendorAddressNFinances));
    dispatch(actions.reset(FormNames.VendorCoverageNLimits));
    dispatch(actions.reset(FormNames.VendorDefaultRatings));
    dispatch(actions.reset(FormNames.VendorGeneralInfo));
  };
}

export function fillInVendorForm(vendor: VendorViewModel) {
  return async (dispatch: AppDispatcher) => {
    if (vendor === null) {
      return;
    }

    let vendorGeneralInfo: Partial<VendorViewModel> = {
      companyName: vendor.companyName,
      emailAddress: vendor.emailAddress,
      dbaName: vendor.dbaName,
      vendorGroup: vendor.vendorGroup,
      businessPhone: vendor.businessPhone,
      businessFax: vendor.businessFax,
      afterHoursPhone: vendor.afterHoursPhone,
      ownerName: vendor.ownerName,
      managerName: vendor.managerName,
      weekdayHours: vendor.weekdayHours,
      saturdayHours: vendor.saturdayHours,
      sundayHours: vendor.sundayHours,
      holidayHours: vendor.holidayHours
    };

    let vendorAddressNFinancesInfo: Partial<VendorViewModel> = {
      address: vendor.address,
      apName: vendor.apName,
      parentId: vendor.parentId,
      vendorType: vendor.vendorType,
      vendorStatus: vendor.vendorStatus,
      licenseNumber: vendor.licenseNumber,
      licenseExpiration: vendor.licenseExpiration,
      taxId: vendor.taxId,
      has1099: vendor.has1099,
      w9Status: vendor.w9Status,
      w9SignedDate: vendor.w9SignedDate,
      w9TaxYear: vendor.w9TaxYear
    };

    let vendorCoverageNLimitsInfo: Partial<VendorViewModel> = {
      liabilityExpiration: vendor.liabilityExpiration,
      liabilityWaiver: vendor.liabilityWaiver,
      sdiExpiration: vendor.sdiExpiration,
      sdiWaiver: vendor.sdiWaiver,
      hourlyRate: vendor.hourlyRate,
      overtimeRate: vendor.overtimeRate,
      weekendRate: vendor.weekendRate,
      emergencyRate: vendor.emergencyRate,
      diagnosticRate: vendor.diagnosticRate,
      secondWorkerRate: vendor.secondWorkerRate,
      authoLimit: vendor.authoLimit,
      agreementType: vendor.agreementType,
      agreementDate: vendor.agreementDate,
      dispatchLimitPermanent: vendor.dispatchLimitPermanent,
      dispatchLimitTemp: vendor.dispatchLimitTemp,
      dispatchLimitTempExpiration: vendor.dispatchLimitTempExpiration,
      suspendedUntil: vendor.suspendedUntil,
      statReport: vendor.statReport,
      vendorPortal: vendor.vendorPortal,
      autoRerating: vendor.autoRerating,
      onlineAutho: vendor.onlineAutho,
      reportCard: vendor.reportCard,
      noBidding: vendor.noBidding
    };

    let defaultRatingsInfo: Partial<VendorViewModel> = {
      invoicePricing: vendor.invoicePricing,
      vendorRelations: vendor.vendorRelations,
      customerRelations: vendor.customerRelations
    };
    dispatch(actions.change(FormNames.VendorGeneralInfo, vendorGeneralInfo));
    dispatch(actions.change(FormNames.VendorAddressNFinances, vendorAddressNFinancesInfo));
    dispatch(actions.change(FormNames.VendorCoverageNLimits, vendorCoverageNLimitsInfo));
    dispatch(actions.change(FormNames.VendorDefaultRatings, defaultRatingsInfo));
  };
}

export function setVendorLoading(loading: boolean): SetVendorLoadingAction {
  return {
    type: VendorActionType.SetVendorLoading,
    isVendorLoading: loading
  };
}

export function getVendorDetails(
  id: number
): (dispatch: AppDispatcher) => Promise<GetVendorDetailsSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    return dispatch(
      requestAction(() => vendorService.getVendorDetails(id), getVendorDetailsSuccess, {
        overrideFailMessage: 'Unable to get Vendor, try again later.',
        loadingAction: setVendorLoading
      })
    );
  };
}

function getVendorDetailsSuccess(vendorVM: VendorViewModel): GetVendorDetailsSuccessAction {
  return {
    type: VendorActionType.GetVendorDetailsSuccess,
    vendorVM: vendorVM
  };
}

export function getExcludedZips(
  id: number,
  tradeId: number,
  tripCharges?: boolean
): (dispatch: AppDispatcher) => Promise<GetExcludedZipsSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    return dispatch(
      requestAction(() => vendorService.getExcludedZips(id, tradeId, tripCharges), getExcludedZipsSuccess, {
        overrideFailMessage: 'Unable to get Vendor Zip Exclusions, try again later.',
        loadingAction: setVendorLoading
      })
    );
  };
}

function getExcludedZipsSuccess(excludedZips: any[]): GetExcludedZipsSuccessAction {
  return {
    type: VendorActionType.GetExcludedZipsSuccess,
    excludedZips: excludedZips
  };
}

export function saveExcludedZips(
  tradeId: number,
  vendorId: number,
  excludedZips: ExcludedZipViewModel[]
): (dispatch: AppDispatcher) => Promise<SaveExcludedZipsSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    dispatch(setZipsSaving(true));
    return dispatch(
      requestAction(() => vendorService.saveExcludedZips(tradeId, vendorId, excludedZips), saveExcludedZipsSuccess, {
        overrideFailMessage: 'Unable to save excluded Vendor Zip Codes, try again later.'
      })
    );
  };
}

export function deleteExcludedZips(
  tradeId: number,
  vendorId: number,
  toBeDeletedZips: ExcludedZipViewModel[]
): (dispatch: AppDispatcher) => Promise<DeleteExcludedZipsSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    dispatch(setZipsSaving(true));
    return dispatch(
      requestAction(
        () => vendorService.deleteExcludedZips(tradeId, vendorId, toBeDeletedZips),
        deleteExcludedZipsSuccess,
        {
          overrideFailMessage: 'Unable to delete Excluded Vendor Zip Codes, try again later.'
        }
      )
    );
  };
}

function saveExcludedZipsSuccess(excludedZips: ExcludedZipViewModel[]): SaveExcludedZipsSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Successfully Saved Excluded Zips'} />,
    ToastOptions.Success
  );
  return {
    type: VendorActionType.SaveExcludedZipsSuccess,
    excludedZips: excludedZips
  };
}

function deleteExcludedZipsSuccess(excludedZips: ExcludedZipViewModel[]): DeleteExcludedZipsSuccessAction {
  return {
    type: VendorActionType.SaveExcludedZipsSuccess,
    excludedZips: excludedZips
  };
}

export function saveIncludedZips(
  vendorId: number,
  includedZips: string[]
): (dispatch: AppDispatcher) => Promise<SaveIncludedZipsSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    dispatch(setZipsSaving(true));
    return dispatch(
      requestAction(() => vendorService.saveIncludedZips(vendorId, includedZips), saveIncludedZipsSuccess, {
        overrideFailMessage: 'Unable to save included Vendor Zip Codes, try again later.'
      })
    );
  };
}

export function deleteIncludedZips(
  vendorId: number,
  toBeDeletedZips: string[]
): (dispatch: AppDispatcher) => Promise<DeleteIncludedZipsSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    dispatch(setZipsSaving(true));
    return dispatch(
      requestAction(() => vendorService.deleteIncludedZips(vendorId, toBeDeletedZips), deleteIncludedZipsSuccess, {
        overrideFailMessage: 'Unable to delete included Vendor Zip Codes, try again later.'
      })
    );
  };
}

export function getVendorFiles(
  vendorId: number
): (dispatch: AppDispatcher) => Promise<GetVendorFilesSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    return dispatch(
      requestAction(() => documentService.getVendorFiles(vendorId), getVendorFilesSuccess, {
        overrideFailMessage: 'Unable to get Vendor files, try again later.'
      })
    );
  };
}

function saveIncludedZipsSuccess(includedZips: string[]): SaveIncludedZipsSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Successfully Saved Included Zips'} />,
    ToastOptions.Success
  );
  return {
    type: VendorActionType.SaveIncludedZipsSuccess,
    includedZips: includedZips
  };
}

function deleteIncludedZipsSuccess(includedZips: string[]): DeleteIncludedZipsSuccessAction {
  return {
    type: VendorActionType.SaveIncludedZipsSuccess,
    includedZips: includedZips
  };
}

export function getZipCountyOptions(
  stateAbbr: string
): (dispatch: AppDispatcher) => Promise<GetZipCountyOptionsSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    dispatch(setVendorLoading(true));
    return dispatch(
      requestAction(() => vendorService.getCityZipOptions(stateAbbr), getZipCountyOptionsSuccess, {
        overrideFailMessage: 'Unable to get Zip Codes, try again later.'
      })
    );
  };
}

export function getExclusionZipCountyOptions(
  stateAbbr: string
): (dispatch: AppDispatcher) => Promise<GetExclusionZipCountyOptionsSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    dispatch(setVendorLoading(true));
    return dispatch(
      requestAction(() => vendorService.getCityZipOptions(stateAbbr), getExclusionZipCountyOptionsSuccess, {
        overrideFailMessage: 'Unable to get Zip Codes, try again later.'
      })
    );
  };
}

function getVendorFilesSuccess(documents: DocumentViewModel[]): GetVendorFilesSuccessAction {
  return {
    type: VendorActionType.GetVendorFilesSuccess,
    documents: documents
  };
}

export function saveVendorFiles(
  vendorId: number,
  fileVMs: FileViewModel[]
): (dispatch: AppDispatcher) => Promise<SaveVendorFilesSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    return dispatch(
      requestAction(
        () => documentService.saveFiles(fileVMs, undefined, undefined, undefined, undefined, vendorId),
        saveVendorFilesSuccess,
        {
          overrideFailMessage: 'Unable to save vendor files, try again later.'
        }
      )
    );
  };
}

export function saveVendorFilesSuccess(vendorId: number, documents: DocumentViewModel[]): SaveVendorFilesSuccessAction {
  return {
    type: VendorActionType.SaveVendorFilesSuccess,
    vendorId: vendorId,
    documents: documents
  };
}

export function removeVendorFile(
  vendorId: number,
  fileId: number
): (dispatch: AppDispatcher) => Promise<RemoveVendorFileSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    return dispatch(
      requestAction(() => documentService.removeFile(fileId), removeVendorFileSuccess, {
        overrideFailMessage: 'Unable to remove vendor file, try again later.'
      })
    );
  };
}

function getZipCountyOptionsSuccess(zipCountyOptions: any): GetZipCountyOptionsSuccessAction {
  return {
    type: VendorActionType.GetZipCountyOptionsSuccess,
    zipCountyOptions: zipCountyOptions
  };
}

function getExclusionZipCountyOptionsSuccess(
  exclusionZipCountyOptions: any
): GetExclusionZipCountyOptionsSuccessAction {
  return {
    type: VendorActionType.GetExclusionZipCountyOptionsSuccess,
    exclusionZipCountyOptions: exclusionZipCountyOptions
  };
}

export function removeVendorFileSuccess(vendorId: number, fileId: number): RemoveVendorFileSuccessAction {
  return {
    type: VendorActionType.RemoveVendorFilesSuccess,
    vendorId: vendorId,
    fileId: fileId
  };
}

export function loadVendorIntoForm() {
  return async (dispatch: Dispatch<AppState>, getStore: () => AppState) => {
    const vendorStore = getStore().vendor;
    if (vendorStore.vendorVM) {
      dispatch(actions.load('vendorForm', vendorStore.vendorVM));
    }
  };
}

export function saveFormData(partialVendorVM: Partial<VendorViewModel>): SaveFormDataAction {
  return {
    type: VendorActionType.SaveFormData,
    partialVendorVM: partialVendorVM
  };
}

export function saveVendor(values: VendorViewModel) {
  return async (dispatch: Dispatch<VendorState>): Promise<SaveVendorSuccessAction | SaveVendorFailureAction> => {
    try {
      dispatch(saveVendorStart());

      const vendor: VendorViewModel = values.id
        ? await vendorService.updateVendor(values)
        : await vendorService.createVendor(values);

      return dispatch(saveVendorSuccess(vendor.id));
    } catch (e) {
      return dispatch(saveVendorFailure(e.toString()));
    }
  };
}

function saveVendorFailure(error: string): SaveVendorFailureAction {
  const errorMessage = `Please ensure that all tabs are valid and completed. \n${error}`;
  toast.success(<ToastComponent type={ToastType.Error} message={errorMessage} />, ToastOptions.Error);
  return {
    type: VendorActionType.SaveVendorFailure,
    errorMessage: error
  };
}

function saveVendorStart(): SaveVendorStartAction {
  return {
    type: VendorActionType.SaveVendorStart
  };
}

function saveVendorSuccess(vendorId: number): SaveVendorSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Successfully Saved Vendor. ID: ' + vendorId} />,
    ToastOptions.Success
  );
  history.push(getRoute(RouteDefinitions.vendor.Details, { id: vendorId }));
  return {
    type: VendorActionType.SaveVendorSuccess,
    createdID: vendorId
  };
}

export interface SetPacketEventsAction extends Action {
  readonly type: VendorActionType.SetPacketEvents;
  readonly vendorPacketEvents: Array<VendorPacketEventViewModel>;
}

export function setPacketEvents(packetEvents: Array<VendorPacketEventViewModel>): SetPacketEventsAction {
  return {
    type: VendorActionType.SetPacketEvents,
    vendorPacketEvents: packetEvents
  };
}

export function getVendorPacketEvents(vendorId: number) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    return await dispatch(
      requestAction(() => vendorService.getVendorPacketEvents(vendorId), setPacketEvents, { hasToast: false })
    );
  };
}

export function setVendorFilesLoading(loading: boolean): SetVendorFilesLoadingAction {
  return {
    type: VendorActionType.SetVendorFilesLoading,
    isLoading: loading
  };
}

export function setZipsSaving(saving: boolean): SetZipsSavingAction {
  return {
    type: VendorActionType.SetZipsSaving,
    isSaving: saving
  };
}

export function updateVendorBrandCertificationsSuccess(
  brandCertifications: OptionsViewModel[]
): UpdateVendorBrandCertificationsSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Successfully Updated Brand Certifications'} />,
    ToastOptions.Success
  );
  return {
    type: VendorActionType.UpdateVendorBrandCertificationsSuccess,
    brandCertifications: brandCertifications
  };
}

export function updateVendorBrandCertifications(
  vendorId: number,
  brandCertifications: OptionsViewModel[]
): (dispatch: AppDispatcher) => Promise<UpdateVendorBrandCertificationsSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    return dispatch(
      requestAction(
        () => vendorService.updateVendorBrandCertifications(vendorId, brandCertifications),
        updateVendorBrandCertificationsSuccess,
        {
          overrideFailMessage: 'Unable to update brand certifications, try again later.'
        }
      )
    );
  };
}

export function getVendorBrandCertificationsSuccess(
  brandCertifications: OptionsViewModel[]
): GetVendorBrandCertificationsSuccessAction {
  return {
    type: VendorActionType.GetVendorBrandCertificationsSuccess,
    brandCertifications: brandCertifications
  };
}
export function getVendorBrandCertificationsStart(): GetVendorBrandCertificationsStartAction {
  return {
    type: VendorActionType.GetVendorBrandCertificationsStart
  };
}
export function getVendorBrandCertifications(
  vendorId: number
): (dispatch: AppDispatcher) => Promise<GetVendorBrandCertificationsSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    await dispatch(getVendorBrandCertificationsStart);
    return await dispatch(
      requestAction(() => vendorService.getVendorBrandCertifications(vendorId), getVendorBrandCertificationsSuccess, {
        overrideFailMessage: 'Unable to retrieve vendor brand certifications, try again later.'
      })
    );
  };
}

export function getEligibleBrandCertifications(): (
  dispatch: AppDispatcher
) => Promise<GetEligibleBrandCertificationsSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    return await dispatch(
      requestAction(() => cviService.getBrands(0), getEligibleBrandCertificationsSuccess, {
        overrideFailMessage: 'Unable to retrieve eligible brand certifications, try again later.'
      })
    );
  };
}

export function getVendorCVIExceptionsStart(): GetVendorCVIExceptionsStartAction {
  return {
    type: VendorActionType.GetVendorCVIExceptionsStart
  };
}

export function getVendorCVIExceptionsSuccess(cvis: OptionsViewModel[]): GetVendorCVIExceptionsSuccessAction {
  return {
    type: VendorActionType.GetVendorCVIExceptionsSuccess,
    cvis: cvis
  };
}

export function getVendorCVIExceptions(
  vendorId: number
): (dispatch: AppDispatcher) => Promise<GetVendorCVIExceptionsSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    await dispatch(getVendorCVIExceptionsStart);
    return await dispatch(
      requestAction(() => vendorService.getVendorCVIExceptions(vendorId), getVendorCVIExceptionsSuccess, {
        overrideFailMessage: 'Unable to retrieve vendor CVI Exceptions, try again later.'
      })
    );
  };
}

export function updateVendorCVIExceptionsSuccess(cvis: OptionsViewModel[]): UpdateVendorCVIExceptionsSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Successfully Updated Brand Certifications'} />,
    ToastOptions.Success
  );
  return {
    type: VendorActionType.UpdateVendorCVIExceptionsSuccess,
    cvis: cvis
  };
}

export function updateVendorCVIExceptions(
  vendorId: number,
  cvis: number[]
): (dispatch: AppDispatcher) => Promise<UpdateVendorCVIExceptionsSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    return dispatch(
      requestAction(() => vendorService.updateVendorCVIExceptions(vendorId, cvis), updateVendorCVIExceptionsSuccess, {
        overrideFailMessage: 'Unable to update CVI exceptions, try again later.'
      })
    );
  };
}

export function deleteVendorCVIExceptionsSuccess(cvis: OptionsViewModel[]): DeleteVendorCVIExceptionsSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Successfully Deleted CVI Certifications'} />,
    ToastOptions.Success
  );
  return {
    type: VendorActionType.DeleteVendorCVIExceptionsSuccess,
    cvis: cvis
  };
}

export function deleteVendorCVIExceptions(
  vendorId: number,
  cvis: number[]
): (dispatch: AppDispatcher) => Promise<DeleteVendorCVIExceptionsSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    return dispatch(
      requestAction(() => vendorService.deleteVendorCVIExceptions(vendorId, cvis), deleteVendorCVIExceptionsSuccess, {
        overrideFailMessage: 'Unable to Delete CVI exceptions, try again later.'
      })
    );
  };
}

export function getVendorBrandExceptionsStart(): GetVendorBrandExceptionsStartAction {
  return {
    type: VendorActionType.GetVendorBrandExceptionsStart
  };
}

export function getVendorBrandExceptionsSuccess(brands: OptionsViewModel[]): GetVendorBrandExceptionsSuccessAction {
  return {
    type: VendorActionType.GetVendorBrandExceptionsSuccess,
    brands: brands
  };
}

export function getVendorBrandExceptions(
  vendorId: number
): (dispatch: AppDispatcher) => Promise<GetVendorBrandExceptionsSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    await dispatch(getVendorBrandExceptionsStart);
    return await dispatch(
      requestAction(() => vendorService.getVendorBrandExceptions(vendorId), getVendorBrandExceptionsSuccess, {
        overrideFailMessage: 'Unable to retrieve vendor Brand Exceptions, try again later.'
      })
    );
  };
}

export function updateVendorBrandExceptionsSuccess(
  brands: OptionsViewModel[]
): UpdateVendorBrandExceptionsSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Successfully Updated Brand Certifications'} />,
    ToastOptions.Success
  );
  return {
    type: VendorActionType.UpdateVendorBrandExceptionsSuccess,
    brands: brands
  };
}

export function updateVendorBrandExceptions(
  vendorId: number,
  brands: number[]
): (dispatch: AppDispatcher) => Promise<UpdateVendorBrandExceptionsSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    return dispatch(
      requestAction(
        () => vendorService.updateVendorBrandExceptions(vendorId, brands),
        updateVendorBrandExceptionsSuccess,
        {
          overrideFailMessage: 'Unable to update Brand exceptions, try again later.'
        }
      )
    );
  };
}

export function saveVendorTradesSuccess(vendorTrades: TradeViewModel[]): SaveVendorTradesSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Successfully Updated Vendor Trades'} />,
    ToastOptions.Success
  );
  return {
    type: VendorActionType.SaveVendorTradesSuccess,
    vendorTrades: vendorTrades
  };
}

export function saveVendorTrades(
  vendorId: number,
  vendorTrades: TradeViewModel[]
): (dispatch: AppDispatcher) => Promise<SaveVendorTradesSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    return dispatch(
      requestAction(() => vendorService.saveVendorTrades(vendorId, vendorTrades), saveVendorTradesSuccess, {
        overrideFailMessage: 'Unable to update Vendor Trades, try again later.'
      })
    );
  };
}

export function updateVendorTradeSuccess(vendorTrade: TradeViewModel): UpdateVendorTradeSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Successfully Updated Vendor Trade'} />,
    ToastOptions.Success
  );
  return {
    type: VendorActionType.UpdateVendorTradeSuccess,
    vendorTrade: vendorTrade
  };
}

export function updateVendorTrade(
  vendorId: number,
  vendorTrade: TradeViewModel
): (dispatch: AppDispatcher) => Promise<UpdateVendorTradeSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    return dispatch(
      requestAction(() => vendorService.updateVendorTrade(vendorId, vendorTrade), updateVendorTradeSuccess, {
        overrideFailMessage: 'Unable to update Vendor Trade, try again later.'
      })
    );
  };
}

export function toggleTradeActivation(
  vendorId: number,
  vendorTradeId: number,
  activate: boolean
): (dispatch: AppDispatcher) => Promise<ToggleTradeActivationSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    await dispatch(ToggleTradeActivationStart());
    return await dispatch(
      requestAction(
        () => vendorService.toggleTradeActivation(vendorId, vendorTradeId, activate),
        toggleTradeActivationSuccess,
        {
          overrideFailMessage: 'Unable to activate or deactivate Vendor Trade, try again later.'
        }
      )
    );
  };
}

function ToggleTradeActivationStart(): ToggleTradeActivationStartAction {
  return {
    type: VendorActionType.ToggleTradeActivationStart
  };
}

export function toggleTradeActivationSuccess(
  vendorTradeId: number,
  activate: boolean
): ToggleTradeActivationSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Successfully toggled Vendor Trade Activation'} />,
    ToastOptions.Success
  );
  return {
    type: VendorActionType.ToggleTradeActivationSuccess,
    vendorTradeId: vendorTradeId,
    activate: activate
  };
}

export function deleteVendorBrandExceptionsSuccess(
  brands: OptionsViewModel[]
): DeleteVendorBrandExceptionsSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Successfully Deleted Brand Certifications'} />,
    ToastOptions.Success
  );
  return {
    type: VendorActionType.DeleteVendorBrandExceptionsSuccess,
    brands: brands
  };
}

export function deleteVendorBrandExceptions(
  vendorId: number,
  brand: number[]
): (dispatch: AppDispatcher) => Promise<DeleteVendorBrandExceptionsSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    return dispatch(
      requestAction(
        () => vendorService.deleteVendorBrandExceptions(vendorId, brand),
        deleteVendorBrandExceptionsSuccess,
        {
          overrideFailMessage: 'Unable to Delete Brand exceptions, try again later.'
        }
      )
    );
  };
}

export function saveVendorPacketEvent(vendorPacketEvent: VendorPacketEventViewModel) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    return await dispatch(
      requestAction(
        () => vendorService.savePacketEvent(vendorPacketEvent),
        () => {
          return { type: '' };
        },
        { hasToast: true, toastSuccessMessage: 'Vendor Packet Event Saved Successfully.' }
      )
    );
  };
}

export function getEligibleBrandCertificationsSuccess(
  brandCertifications: OptionsViewModel[]
): GetEligibleBrandCertificationsSuccessAction {
  return {
    type: VendorActionType.GetEligibleBrandCertificationsSuccess,
    brandCertifications: brandCertifications as OptionsViewModel[]
  };
}

export async function searchZipCodes(vendorId: number, query: string, excludeZips?: boolean) {
  let data = await vendorService.searchZipCodes(vendorId, 1, 50, query, excludeZips);
  return {
    ...data,
    results: data.results.map(result => {
      return { value: result, label: result } as Option<string>;
    })
  };
}

export function getTripCharges(
  id: number,
  tradeId: number
): (dispatch: AppDispatcher) => Promise<SetTripChargesAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    return dispatch(
      requestAction(() => vendorService.getExcludedZips(id, tradeId, true), setTripCharges, {
        overrideFailMessage: 'Unable to get Vendor Trade Trip Charges, try again later.',
        loadingAction: setVendorLoading
      })
    );
  };
}

function setTripCharges(tripCharges: ExcludedZipViewModel[]): SetTripChargesAction {
  return {
    type: VendorActionType.SetTripCharges,
    tripCharges: tripCharges
  };
}



// WEBPACK FOOTER //
// ./src/vendor/vendor.actions.tsx